import { useLayoutEffect, useRef, useState, type ReactNode } from "react"
import { layoutMargin } from "@/lib/prompt-margin"

const GAP = 12

export type MarginEntry = { id: string; top: number; node: ReactNode }

type MarginProps = {
  entries: MarginEntry[]
  pinned: string | null
  minHeight: number
}

export function CommentMargin({ entries, pinned, minHeight }: MarginProps) {
  const refs = useRef(new Map<string, HTMLDivElement>())
  const [heights, setHeights] = useState<Record<string, number>>({})

  useLayoutEffect(() => {
    const observer = new ResizeObserver(() => {
      const next: Record<string, number> = {}
      refs.current.forEach((element, id) => {
        next[id] = element.offsetHeight
      })
      setHeights((previous) => {
        const same = Object.keys(next).length === Object.keys(previous).length &&
          Object.entries(next).every(([id, height]) => previous[id] === height)
        return same ? previous : next
      })
    })
    refs.current.forEach((element) => observer.observe(element))
    return () => observer.disconnect()
  }, [entries])

  const placed = layoutMargin(
    entries.map((entry) => ({ id: entry.id, top: entry.top, height: heights[entry.id] ?? 120 })),
    GAP,
    pinned,
  )
  const lowest = entries.reduce(
    (bottom, entry) => Math.max(bottom, (placed[entry.id] ?? 0) + (heights[entry.id] ?? 120)), 0,
  )

  return (
    <div className="relative" style={{ minHeight: Math.max(minHeight, lowest + GAP) }}>
      {entries.map((entry) => (
        <div
          key={entry.id}
          ref={(element) => {
            if (element) refs.current.set(entry.id, element)
            else refs.current.delete(entry.id)
          }}
          className="absolute inset-x-0 transition-[top] duration-200 ease-out motion-reduce:transition-none"
          style={{ top: placed[entry.id] ?? entry.top }}
        >
          {entry.node}
        </div>
      ))}
    </div>
  )
}
